import React, { useEffect, useRef, useState } from "react";
import { api } from "../../../software/api";
import { toast } from "sonner";
import { FlaskConical, Plus, Trash2, Pencil, X, Copy, Package } from "lucide-react";

const ROLES = [
  ["sanitiser", "Sanitiser"], ["oxidiser", "Oxidiser / Shock"], ["acid", "pH Down (Acid)"], ["ph_up", "pH Up"],
  ["alk_up", "Alkalinity Up"], ["cal_up", "Calcium Up"], ["cl_down", "Chlorine Reducer"], ["stab", "Stabiliser"],
  ["phos", "Phosphate Remover"], ["salt", "Salt"], ["mineral", "Mineral"], ["clarifier", "Clarifier"],
  ["filter_cleaner", "Filter Cleaner"], ["foam", "Foam / Surge Control"],
  ["metal", "Metals / Copper"], ["metal_stain", "Metal Stains"], ["general_stain", "General Stains"], ["floc", "Flocculants"], ["scale", "Scale Control (high calcium)"],
];
const RANGE_CATS = [["pool", "Pools"], ["spa", "Spas"], ["swimspa", "Swim Spas / Urban Pools"]];
const emptyMatrix = () => ({ pool: {}, spa: {}, swimspa: {} });
const blank = () => ({ name: "", owner: "", active: true, matrix: emptyMatrix(), notes: "" });
const clone = (o) => JSON.parse(JSON.stringify(o || {}));

// Search the Parts table to link a pack size to its price + part number.
// Pass value/onText for a controlled free-text field (product name) that still autocompletes from Parts.
export function PartPicker({ initial, onPick, value, onText, placeholder = "Search Parts…", testid = "cr-part-search", inputClass = "rounded border border-slate-200 px-2 py-1 text-[12px]" }) {
  const [own, setOwn] = useState(initial || "");
  const q = value !== undefined ? value : own;
  const setQ = (v) => (onText ? onText(v) : setOwn(v));
  const [res, setRes] = useState([]);
  const [open, setOpen] = useState(false);
  const t = useRef(null);
  const search = (v) => {
    setQ(v); clearTimeout(t.current);
    if (v.trim().length < 2) { setRes([]); setOpen(false); return; }
    t.current = setTimeout(() => api("GET", `/lookups/parts?q=${encodeURIComponent(v.trim())}`).then((r) => { setRes(r || []); setOpen(true); }).catch(() => setRes([])), 220);
  };
  return (
    <div className="relative flex-1 min-w-0">
      <input value={q} onChange={(e) => search(e.target.value)} onFocus={() => res.length && setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 200)} placeholder={placeholder} data-testid={testid} className={`w-full ${inputClass} focus:border-sky-400 outline-none`} />
      {open && res.length > 0 && (
        <div className="absolute z-40 mt-0.5 w-72 max-h-44 overflow-auto rounded-md bg-white border border-slate-300 shadow-xl">
          {res.map((p, idx) => (
            <button key={idx} type="button" data-testid={`${testid}-opt-${idx}`} onMouseDown={(e) => e.preventDefault()} onClick={() => { onPick(p); if (value === undefined) setQ(p.name); setOpen(false); }} className="block w-full text-left px-2 py-1 text-[12px] hover:bg-sky-50">
              {p.name} <span className="text-emerald-700 font-bold">${Number(p.price).toFixed(2)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// total products across all categories (for the card count)
const countProducts = (m) => RANGE_CATS.reduce((n, [c]) => n + ROLES.reduce((s, [k]) => s + ((m?.[c]?.[k] || []).length), 0), 0);
// primary (first) products in the Pools column — shown as chips on the card
const poolChips = (m) => ROLES.map(([k, lbl]) => [lbl, (m?.pool?.[k] || [])[0]?.name]).filter(([, v]) => v);

export default function ChemicalRanges() {
  const [ranges, setRanges] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api("GET", "/chemical-ranges").then(setRanges).catch(() => setRanges([]));
  useEffect(() => { load(); }, []);

  const save = async (r) => {
    if (!r.name.trim()) { toast.error("Range name required"); return; }
    try {
      if (r.id) await api("PATCH", `/chemical-ranges/${r.id}`, r);
      else await api("POST", "/chemical-ranges", r);
      toast.success("Range saved"); setEditing(null); load();
    } catch (e) { toast.error(e.message || "Could not save"); }
  };
  const remove = async (r) => { if (!window.confirm(`Delete the "${r.name}" range?`)) return; await api("DELETE", `/chemical-ranges/${r.id}`); load(); };

  return (
    <div data-testid="chemical-ranges">
      <div className="flex items-center justify-between mb-1">
        <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2"><FlaskConical className="h-6 w-6 text-sky-600" /> Chemical Ranges</h1>
        <button onClick={() => setEditing(blank())} data-testid="cr-new-btn" className="inline-flex items-center gap-2 rounded-full bg-sky-600 hover:bg-sky-700 text-white font-bold px-5 py-2.5 text-sm"><Plus className="h-4 w-4" /> New Range</button>
      </div>
      <p className="text-sm text-slate-500 mb-5">Each brand carries its own products for every chemistry job — and you can set <b>different products for Pools, Spas and Swim Spas / Urban Pools</b>, with several substitute products per job.</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {ranges.map((r) => {
          const chips = poolChips(r.matrix);
          return (
            <div key={r.id} data-testid={`cr-card-${r.id}`} className="rounded-2xl border-2 border-slate-200 bg-white p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display font-black text-lg text-slate-900">{r.name}</div>
                  <div className="text-[12px] text-slate-500">{r.owner || "—"} · {countProducts(r.matrix)} products · Pools / Spas / Swim Spas</div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setEditing({ ...r, matrix: clone(r.matrix || emptyMatrix()) })} data-testid={`cr-edit-${r.id}`} className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50"><Pencil className="h-4 w-4 text-slate-600" /></button>
                  <button onClick={() => remove(r)} data-testid={`cr-delete-${r.id}`} className="p-1.5 rounded-lg border border-slate-200 hover:bg-red-50"><Trash2 className="h-4 w-4 text-red-500" /></button>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1">
                {chips.slice(0, 6).map(([lbl, v]) => (
                  <span key={lbl} className="text-[11px] px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200" title={lbl}>{v}</span>
                ))}
                {chips.length > 6 && <span className="text-[11px] px-2 py-0.5 text-slate-400">+{chips.length - 6} more</span>}
              </div>
              {!r.active && <span className="inline-block mt-2 text-[11px] font-bold text-slate-400">Inactive</span>}
            </div>
          );
        })}
        {ranges.length === 0 && <p className="text-slate-400 text-sm col-span-full">No ranges yet — click New Range.</p>}
      </div>

      {editing && <RangeEditor r={editing} onChange={setEditing} onSave={save} onClose={() => setEditing(null)} />}
    </div>
  );
}

function RangeEditor({ r, onChange, onSave, onClose }) {
  const [cat, setCat] = useState("pool");
  const [openPacks, setOpenPacks] = useState({});
  const m = r.matrix || emptyMatrix();

  const mutate = (role, fn) => {
    const nm = clone(r.matrix || emptyMatrix());
    nm[cat] = nm[cat] || {};
    nm[cat][role] = nm[cat][role] || [];
    fn(nm[cat][role]);
    onChange({ ...r, matrix: nm });
  };
  const addProduct = (role) => mutate(role, (l) => l.push({ name: "", form: "" }));
  const setField = (role, i, field, val) => mutate(role, (l) => { l[i] = { ...l[i], [field]: val }; });
  const removeProduct = (role, i) => mutate(role, (l) => l.splice(i, 1));
  const addPack = (role, i) => mutate(role, (l) => { l[i].packs = l[i].packs || []; l[i].packs.push({ part_id: "", label: "", size: "", unit: "kg", price: "" }); });
  const setPack = (role, i, j, field, val) => mutate(role, (l) => { l[i].packs[j] = { ...l[i].packs[j], [field]: val }; });
  const pickPart = (role, i, j, p) => mutate(role, (l) => { l[i].packs[j] = { ...l[i].packs[j], part_id: p.part_id, name: p.name, label: p.name, price: p.price,
    ...(p.size ? { size: String(p.size), unit: p.unit } : {}) }; });
  // Product name picked from Parts: base name (pack size stripped) + part link; the picked size becomes a pack.
  const pickProduct = (role, i, p) => mutate(role, (l) => {
    const base = p.size ? p.name.replace(/\s*[-–]?\s*\d+(\.\d+)?\s*(kg|kgs|g|gm|gms|grams?|l|lt|ltr|litres?|ml|mls)\s*$/i, "").trim() : p.name;
    const packs = [...(l[i].packs || [])];
    if (p.size && !packs.some((x) => x.part_id === p.part_id)) packs.push({ part_id: p.part_id, name: p.name, label: p.name, price: p.price, size: String(p.size), unit: p.unit });
    l[i] = { ...l[i], name: base || p.name, part_id: p.part_id, price: p.price, packs };
  });
  const removePack = (role, i, j) => mutate(role, (l) => l[i].packs.splice(j, 1));
  const toggleOpen = (key) => setOpenPacks((s) => ({ ...s, [key]: !s[key] }));
  const copyFromPool = () => {
    if (cat === "pool") return;
    const nm = clone(r.matrix || emptyMatrix());
    nm[cat] = clone(nm.pool || {});
    onChange({ ...r, matrix: nm });
    toast.success(`Copied Pools products into ${RANGE_CATS.find((c) => c[0] === cat)[1]}`);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" data-testid="cr-editor">
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl max-h-[92vh] overflow-auto">
        <div className="flex justify-between items-center p-4 border-b border-slate-100 sticky top-0 bg-white z-10">
          <span className="font-display font-bold text-slate-900">{r.id ? "Edit" : "New"} Chemical Range{r.name ? ` — ${r.name}` : ""}</span>
          <button onClick={onClose} className="rounded-full border border-slate-200 p-1.5 hover:bg-slate-50"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Brand / range name</label>
              <input value={r.name} onChange={(e) => onChange({ ...r, name: e.target.value })} data-testid="cr-name" className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-sky-400 outline-none" placeholder="e.g. Bioguard" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Made by (optional)</label>
              <input value={r.owner} onChange={(e) => onChange({ ...r, owner: e.target.value })} data-testid="cr-owner" className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-sky-400 outline-none" placeholder="e.g. BioLab Australia" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <input type="checkbox" checked={r.active} onChange={(e) => onChange({ ...r, active: e.target.checked })} data-testid="cr-active" className="h-4 w-4" /> Active (untick if brand no longer sold)
          </label>

          {/* category tabs */}
          <div className="flex items-center gap-2 border-b border-slate-200">
            {RANGE_CATS.map(([v, l]) => (
              <button key={v} onClick={() => setCat(v)} data-testid={`cr-cat-tab-${v}`}
                className={`px-4 py-2 text-sm font-bold -mb-px border-b-2 ${cat === v ? "border-sky-600 text-sky-700" : "border-transparent text-slate-400 hover:text-slate-600"}`}>{l}</button>
            ))}
            {cat !== "pool" && (
              <button onClick={copyFromPool} data-testid="cr-copy-from-pool" className="ml-auto inline-flex items-center gap-1 text-[12px] font-bold text-slate-500 hover:text-sky-600 px-2 py-1"><Copy className="h-3.5 w-3.5" /> Copy from Pools</button>
            )}
          </div>

          <p className="text-xs font-bold text-slate-500">Add one or more products per chemistry job for <span className="text-sky-700">{RANGE_CATS.find((c) => c[0] === cat)[1]}</span>. The first product is the one printed on the water-test report; the rest are stocked substitutes.</p>

          <div className="space-y-3">
            {ROLES.map(([k, lbl]) => {
              const items = m?.[cat]?.[k] || [];
              return (
                <div key={k} className="rounded-xl border border-slate-200 p-3" data-testid={`cr-role-block-${k}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[13px] font-black text-slate-700">{lbl}</span>
                    <button onClick={() => addProduct(k)} data-testid={`cr-add-${k}`} className="inline-flex items-center gap-1 text-[12px] font-bold text-sky-600 hover:text-sky-800"><Plus className="h-3.5 w-3.5" /> Add product</button>
                  </div>
                  {items.length === 0 && <p className="text-[12px] text-slate-400 italic">No products set for this job.</p>}
                  <div className="space-y-1.5">
                    {items.map((it, i) => {
                      const key = `${cat}-${k}-${i}`; const open = !!openPacks[key]; const packs = it.packs || [];
                      return (
                        <div key={i} className="rounded-md border border-slate-100 bg-slate-50/60 p-1.5">
                          <div className="flex items-center gap-2">
                            <PartPicker value={it.name || ""} onText={(v) => setField(k, i, "name", v)} onPick={(p) => pickProduct(k, i, p)} placeholder="Product name (type to search Parts)" testid={`cr-${cat}-${k}-name-${i}`} inputClass="rounded-lg border border-slate-200 px-2 py-1.5 text-[13px]" />
                            <input value={it.form || ""} onChange={(e) => setField(k, i, "form", e.target.value)} data-testid={`cr-${cat}-${k}-form-${i}`} placeholder="Form (e.g. Liquid, Granular)" className="w-48 shrink-0 rounded-lg border border-slate-200 px-2 py-1.5 text-[13px] text-slate-600 focus:border-sky-400 outline-none" />
                            <button onClick={() => toggleOpen(key)} data-testid={`cr-packs-toggle-${cat}-${k}-${i}`} className={`shrink-0 inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1.5 rounded-lg border ${packs.length ? "border-sky-300 text-sky-700 bg-sky-50" : "border-slate-200 text-slate-500"}`}><Package className="h-3.5 w-3.5" />{packs.length ? `Packs (${packs.length})` : "Packs"}</button>
                            <button onClick={() => removeProduct(k, i)} data-testid={`cr-remove-${cat}-${k}-${i}`} className="p-1.5 rounded-lg border border-slate-200 hover:bg-red-50 shrink-0"><Trash2 className="h-3.5 w-3.5 text-red-500" /></button>
                          </div>
                          {open && (
                            <div className="mt-1.5 ml-1 border-l-2 border-sky-200 pl-2 space-y-1" data-testid={`cr-packs-${cat}-${k}-${i}`}>
                              <div className="text-[10px] text-slate-400">Link the stocked pack sizes — the shopping list rounds the dose up to the best pack(s). Leave empty for products not sold by size.</div>
                              {packs.map((pk, j) => (
                                <div key={j} className="flex items-center gap-1.5">
                                  <PartPicker initial={pk.name || pk.label} onPick={(p) => pickPart(k, i, j, p)} />
                                  <input value={pk.label || ""} onChange={(e) => setPack(k, i, j, "label", e.target.value)} placeholder="Label" title={pk.label || ""} data-testid={`cr-pack-label-${cat}-${k}-${i}-${j}`} className="w-32 shrink-0 rounded border border-slate-200 px-2 py-1 text-[12px] outline-none focus:border-sky-400" />
                                  <input value={pk.size ?? ""} onChange={(e) => setPack(k, i, j, "size", e.target.value)} placeholder="Size" inputMode="decimal" data-testid={`cr-pack-size-${cat}-${k}-${i}-${j}`} className="w-14 shrink-0 rounded border border-slate-200 px-2 py-1 text-[12px] outline-none focus:border-sky-400" />
                                  <select value={pk.unit || "kg"} onChange={(e) => setPack(k, i, j, "unit", e.target.value)} className="w-16 shrink-0 rounded border border-slate-200 px-1 py-1 text-[12px]">{["kg", "g", "L", "mls"].map((u) => <option key={u}>{u}</option>)}</select>
                                  <span className="w-16 shrink-0 text-right text-[11px] font-bold text-emerald-700">${Number(pk.price || 0).toFixed(2)}</span>
                                  <button onClick={() => removePack(k, i, j)} data-testid={`cr-remove-pack-${cat}-${k}-${i}-${j}`} className="p-1 rounded border border-slate-200 hover:bg-red-50 shrink-0"><Trash2 className="h-3 w-3 text-red-500" /></button>
                                </div>
                              ))}
                              <button onClick={() => addPack(k, i)} data-testid={`cr-add-pack-${cat}-${k}-${i}`} className="inline-flex items-center gap-1 text-[11px] font-bold text-sky-600 hover:text-sky-800"><Plus className="h-3 w-3" /> Add pack size</button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="p-4 border-t border-slate-100 flex gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="flex-1 rounded-full border-2 border-slate-200 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-50">Cancel</button>
          <button onClick={() => onSave(r)} data-testid="cr-save" className="flex-1 rounded-full bg-sky-600 hover:bg-sky-700 text-white py-2.5 text-sm font-bold">Save range</button>
        </div>
      </div>
    </div>
  );
}
